/**
 * File: components/InviteUserModal.jsx
 * =====================================
 * Modal for inviting users to properties
 */

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext.jsx';

const ROLES = [
  {
    id: 'co_owner',
    name: 'Co-Owner',
    description: 'Full access to everything including financials and user management'
  },
  {
    id: 'family_member',
    name: 'Family Member',
    description: 'Can view most data, edit tasks, and approve estimates'
  },
  {
    id: 'service_provider',
    name: 'Service Provider',
    description: 'Can only see and update tasks assigned to them'
  }
];

const SCOPES = [
  { id: 'portfolio', name: 'All My Properties', description: 'Access to all properties you own' },
  { id: 'property', name: 'Specific Property', description: 'Access to one property only' }
];

export default function InviteUserModal({ isOpen, onClose, properties = [] }) {
  const { getToken } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [scope, setScope] = useState('portfolio');
  const [propertyId, setPropertyId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const resetForm = () => {
    setStep(1);
    setEmail('');
    setRole('');
    setScope('portfolio');
    setPropertyId('');
    setMessage('');
    setError('');
    setSuccess(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      const token = getToken();
      const response = await fetch('/api/invites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email,
          role,
          scope,
          propertyId: scope === 'property' ? propertyId : undefined,
          message
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitation');
      }

      setSuccess({
        email: data.email,
        inviteUrl: data.inviteUrl
      });
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 4 ? 'Invitation Sent!' : 'Invite User'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Select Role */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-gray-600 text-sm mb-4">
                What role should this person have?
              </p>
              {ROLES.map(r => (
                <button
                  key={r.id}
                  onClick={() => {
                    setRole(r.id);
                    setStep(2);
                  }}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    role === r.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{r.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{r.description}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2: Select Scope (for service providers) */}
          {step === 2 && (
            <div className="space-y-4">
              <button
                onClick={() => setStep(1)}
                className="text-indigo-600 text-sm mb-2 flex items-center gap-1"
              >
                ← Back
              </button>
              <p className="text-gray-600 text-sm mb-4">
                {role === 'service_provider'
                  ? 'Which properties should they have access to?'
                  : 'Select access scope:'}
              </p>
              {SCOPES.map(s => (
                <button
                  key={s.id}
                  onClick={() => {
                    setScope(s.id);
                    if (s.id === 'portfolio' || properties.length === 1) {
                      if (properties.length === 1) {
                        setPropertyId(properties[0].id);
                      }
                      setStep(3);
                    } else {
                      setStep(2.5);
                    }
                  }}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    scope === s.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{s.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{s.description}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 2.5: Select Property */}
          {step === 2.5 && (
            <div className="space-y-4">
              <button
                onClick={() => setStep(2)}
                className="text-indigo-600 text-sm mb-2 flex items-center gap-1"
              >
                ← Back
              </button>
              <p className="text-gray-600 text-sm mb-4">
                Select a property:
              </p>
              {properties.map(p => (
                <button
                  key={p.id}
                  onClick={() => {
                    setPropertyId(p.id);
                    setStep(3);
                  }}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    propertyId === p.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="font-medium text-gray-900">{p.name || p.address}</div>
                  <div className="text-sm text-gray-500 mt-1">{p.address}</div>
                </button>
              ))}
            </div>
          )}

          {/* Step 3: Enter Email */}
          {step === 3 && (
            <div className="space-y-4">
              <button
                onClick={() => setStep(scope === 'property' ? 2.5 : 2)}
                className="text-indigo-600 text-sm mb-2 flex items-center gap-1"
              >
                ← Back
              </button>

              <div className="p-3 bg-gray-50 rounded-lg text-sm mb-4">
                <div className="text-gray-500">Inviting as</div>
                <div className="font-medium text-gray-900">
                  {ROLES.find(r => r.id === role)?.name}
                  {scope === 'portfolio' && ' (All Properties)'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Personal message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hi! I'd like to invite you to..."
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <button
                onClick={handleSubmit}
                disabled={!email || loading}
                className={`w-full py-3 px-4 rounded-lg font-medium text-white ${
                  !email || loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && success && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Invitation Sent!</h3>
                <p className="text-gray-600 mt-1">
                  We've sent an invitation to <strong>{success.email}</strong>
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-left">
                <div className="text-sm text-gray-500 mb-1">Share this link directly:</div>
                <div className="text-sm font-mono bg-white p-2 rounded border break-all">
                  {success.inviteUrl}
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-full py-3 px-4 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
